import { useState, useEffect, useCallback, RefObject } from "react";

const DEFAULT_THRESHOLD = 300;

/**
 * Hook to show a "scroll to top" button when the user has scrolled past a threshold.
 * Works with any scrollable container (e.g. main content area, a div with overflow-auto).
 *
 * @param scrollContainerRef - Ref to the scrollable element. If null/undefined, no listener is attached.
 * @param threshold - Pixels scrolled before showing the button (default 300).
 * @returns { showButton, scrollToTop }
 */
export function useScrollToTop(
  scrollContainerRef: RefObject<HTMLElement | null> | null | undefined,
  threshold: number = DEFAULT_THRESHOLD
): { showButton: boolean; scrollToTop: () => void } {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;

    const handleScroll = () => {
      setShowButton(el.scrollTop > threshold);
    };

    el.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef, threshold]);

  const scrollToTop = useCallback(() => {
    const el = scrollContainerRef?.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [scrollContainerRef]);

  return { showButton, scrollToTop };
}
